import { Add, Delete } from '@mui/icons-material';
import {
  Autocomplete,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getCostCenters } from '../../../../../../../../lib/api/accounting';
import CustomButton from '../../../../../../../../lib/designSystem/Button';
import CustomAutocomplete from '../../../../../../../../lib/designSystem/CustomAutocomplete';
import { formatMoney } from '../../../../../../../../lib/functions/utils';
import {
  AccountingEntryLine,
  AccountingNode,
  CostCenter,
  EditEntryWithLinesProps,
  EntryWithLinesHandleChangeEvent,
} from '../../../../types';
import { handleEnterPress as handleEnterPressNavigation } from './useFieldNavigation';

const EditEntryWithLines = (props: EditEntryWithLinesProps) => {
  const { entry, setEntry, nodes, free } = props;
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [nodeOptions, setNodeOptions] = useState<AccountingNode[]>([]);

  const dispatch = useDispatch();

  const fieldRefs = useRef<
    Map<
      number,
      {
        entryName?: React.RefObject<HTMLInputElement>;
        account?: React.RefObject<HTMLInputElement>;
        description?: React.RefObject<HTMLInputElement>;
        costCenter?: React.RefObject<HTMLInputElement>;
        documentKey?: React.RefObject<HTMLInputElement | HTMLDivElement>;
        businessIdentifier?: React.RefObject<HTMLInputElement>;
        debit?: React.RefObject<HTMLInputElement>;
        credit?: React.RefObject<HTMLInputElement>;
      }
    >
  >(new Map());

  const getLineRefs = (lineIndex: number) => {
    if (!fieldRefs.current.has(lineIndex)) {
      fieldRefs.current.set(lineIndex, {
        entryName: React.createRef<HTMLInputElement>(),
        account: React.createRef<HTMLInputElement>(),
        description: React.createRef<HTMLInputElement>(),
        costCenter: React.createRef<HTMLInputElement>(),
        documentKey: React.createRef<HTMLInputElement | HTMLDivElement>(),
        businessIdentifier: React.createRef<HTMLInputElement>(),
        debit: React.createRef<HTMLInputElement>(),
        credit: React.createRef<HTMLInputElement>(),
      });
    }
    return fieldRefs.current.get(lineIndex)!;
  };

  const handleChange = (
    e: EntryWithLinesHandleChangeEvent,
    lineIndex: number,
    attribute: string,
  ) => {
    let value = e.target.value;
    if (value === null || value === undefined) return;

    // Convert to string for processing
    const stringValue = String(value);

    if (attribute === 'debit' || attribute === 'credit') {
      value =
        parseInt(stringValue.split('.').join('').split('$').join('')) || 0;
    }
    const newEntry = { ...entry };
    newEntry.lines = newEntry.lines.map(
      (line: AccountingEntryLine, index: number) => {
        if (index === lineIndex) {
          return { ...line, [attribute]: value };
        }
        return line;
      },
    );
    setEntry(newEntry);
  };

  const handleDeleteLine = (lineIndex: number) => {
    const newEntry = { ...entry };
    newEntry.lines = newEntry.lines.filter(
      (line: AccountingEntryLine, index: number) => index !== lineIndex,
    );
    setEntry(newEntry);
  };

  const handleAddLine = () => {
    const newLine = {
      nodeId: null,
      debit: 0,
      credit: 0,
      costCenterId: null,
      documentKey: '',
      businessIdentifier: entry?.lines?.[0]?.businessIdentifier || null,
    };
    setEntry({ ...entry, lines: [...entry.lines, newLine] });
    setTimeout(() => {
      const newLineIndex = entry.lines.length;
      const newLineRefs = getLineRefs(newLineIndex);
      newLineRefs.account?.current?.focus();
    }, 0);
  };

  const totalSumEquals = entry.lines.reduce(
    (acc: number, line: AccountingEntryLine) => {
      return acc + line.debit - line.credit;
    },
    0,
  );

  const fetchCostCenters = async () => {
    try {
      const response = await getCostCenters(dispatch);
      setCostCenters(response);
    } catch (error) {
      setCostCenters([]);
    }
  };

  useEffect(() => {
    fetchCostCenters().catch(() => setCostCenters([]));
  }, []);
  useEffect(() => {
    const sortedOptions = nodes.sort(
      (a: AccountingNode, b: AccountingNode) =>
        a.accountCode?.localeCompare(b.accountCode || '') || 0,
    );
    setNodeOptions(sortedOptions);
  }, [nodes]);

  const getSelectedNode = (lineIndex: number) => {
    const line = entry.lines[lineIndex];
    return nodes.find((node: AccountingNode) => node.id === line.nodeId);
  };

  // Wrapper for handleEnterPressNavigation with current context
  const handleEnterPress = (lineIndex: number, currentField: string) => {
    handleEnterPressNavigation({
      lineIndex,
      currentField,
      entry,
      nodes,
      free,
      handleAddLine,
      getLineRefs,
    });
  };

  // Función de filtrado personalizado que prioriza coincidencias exactas
  const filterNodeOptions = (options: AccountingNode[], inputValue: string) => {
    if (!inputValue) {
      return options;
    }

    const searchText = inputValue.toLowerCase().trim();

    // Separar opciones por tipo de coincidencia
    const exactMatches: AccountingNode[] = [];
    const startsWithMatches: AccountingNode[] = [];
    const containsMatches: AccountingNode[] = [];

    options.forEach((option) => {
      const accountCode = option.accountCode?.toLowerCase() || '';
      const key = option.key?.toLowerCase() || '';

      // Coincidencia exacta en accountCode o key
      if (accountCode === searchText || key === searchText) {
        exactMatches.push(option);
      }
      // Empieza con el texto en accountCode o key
      else if (
        accountCode.startsWith(searchText) ||
        key.startsWith(searchText)
      ) {
        startsWithMatches.push(option);
      }
      // Contiene el texto en accountCode o key
      else if (accountCode.includes(searchText) || key.includes(searchText)) {
        containsMatches.push(option);
      }
    });

    // Retornar ordenado: exactas primero, luego las que empiezan, luego las que contienen
    return [...exactMatches, ...startsWithMatches, ...containsMatches];
  };

  return (
    <div style={{ padding: '20px 0px' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        {free ? (
          <TextField
            label="Glosa asiento"
            value={entry.name}
            sx={{ minWidth: '400px' }}
            onChange={(e) => setEntry({ ...entry, name: e.target.value })}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (entry.lines.length > 0) {
                  const lineRefs = getLineRefs(0);
                  lineRefs.account?.current?.focus();
                } else if (entry.name) {
                  // Si hay glosa pero no hay líneas, crear la primera línea y enfocar
                  const newLine = {
                    nodeId: null,
                    debit: 0,
                    credit: 0,
                    costCenterId: null,
                    documentKey: '',
                    businessIdentifier: null,
                  };
                  setEntry({ ...entry, lines: [newLine] });
                  setTimeout(() => {
                    const lineRefs = getLineRefs(0);
                    lineRefs.account?.current?.focus();
                  }, 0);
                }
              }
            }}
            inputRef={getLineRefs(0).entryName}
          />
        ) : (
          <div>
            <Typography variant="body2">Glosa asiento:</Typography>
            <Typography variant="h6">{entry.name}</Typography>
          </div>
        )}
        <div>
          <CustomButton onClick={handleAddLine}>
            <Add />
          </CustomButton>
        </div>
      </div>

      <div style={{ padding: '10px 0px' }}>
        {entry.lines.map((line: AccountingEntryLine, index: number) => {
          const selectedNode = getSelectedNode(index) || null;
          return (
            <Grid
              container
              spacing={2}
              style={{ padding: '4px 0px' }}
              key={line.id || index}
            >
              <Grid item xs={2}>
                <LightTooltip
                  placement="right"
                  title={
                    <div style={{ padding: 10 }}>
                      <Typography variant="body1">
                        {selectedNode?.accountCode} - {selectedNode?.key}
                      </Typography>
                    </div>
                  }
                >
                  <div>
                    <CustomAutocomplete<AccountingNode>
                      options={nodeOptions}
                      value={selectedNode}
                      onChange={(newValue) => {
                        handleChange(
                          {
                            target: {
                              value: newValue ? newValue.id : null,
                            },
                          },
                          index,
                          'nodeId',
                        );
                      }}
                      getOptionLabel={(option) =>
                        `${option.accountCode} - ${option.key}` || ''
                      }
                      filterOptions={filterNodeOptions}
                      label="Cuenta"
                      placeholder="Buscar cuenta..."
                      fullWidth
                      ref={getLineRefs(index).account}
                      onEnterPress={() => {
                        // Use setTimeout to ensure state is updated after node selection
                        setTimeout(() => {
                          handleEnterPress(index, 'account');
                        }, 0);
                      }}
                    />
                  </div>
                </LightTooltip>
              </Grid>
              <Grid item xs={3}>
                <TextField
                  style={{ textAlign: 'right' }}
                  label="Glosa"
                  value={line.description || ''}
                  onChange={(e) => handleChange(e, index, 'description')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnterPress(index, 'description');
                    }
                  }}
                  inputRef={getLineRefs(index).description}
                />
              </Grid>
              <Grid item xs={1}>
                {selectedNode?.costCenterRequired ? (
                  <Autocomplete
                    fullWidth
                    options={costCenters}
                    getOptionLabel={(costCenter: CostCenter) => costCenter.name}
                    value={
                      costCenters.find(
                        (cc: CostCenter) => cc.id === line.costCenterId,
                      ) || null
                    }
                    onChange={(e, newValue) =>
                      handleChange(
                        {
                          target: { value: newValue ? newValue.id : null },
                        },
                        index,
                        'costCenterId',
                      )
                    }
                    onClose={() => {
                      // When autocomplete closes, advance to next field
                      setTimeout(() => {
                        handleEnterPress(index, 'costCenter');
                      }, 100);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Centro de Costo"
                        required
                        inputRef={getLineRefs(index).costCenter}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleEnterPress(index, 'costCenter');
                          }
                        }}
                      />
                    )}
                  />
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={1}>
                {selectedNode?.documentKeyRequired ? (
                  <FormControl fullWidth>
                    <InputLabel id={`document-key-label-${index}`}>
                      Tipo Doc.
                    </InputLabel>
                    <Select
                      value={line.documentKey || ''}
                      onChange={(e) => handleChange(e, index, 'documentKey')}
                      label="Tipo Doc."
                      labelId={`document-key-label-${index}`}
                      required
                      data-line-index={index}
                      data-field="documentKey"
                      onClose={() => {
                        // When select closes, advance to next field
                        setTimeout(() => {
                          handleEnterPress(index, 'documentKey');
                        }, 100);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          // Close select and advance
                          setTimeout(() => {
                            handleEnterPress(index, 'documentKey');
                          }, 100);
                        }
                      }}
                    >
                      {/* TODO: Add other document types */}
                      <MenuItem value="electronic-invoice">Factura</MenuItem>
                      <MenuItem value="otro">Otro</MenuItem>
                    </Select>
                  </FormControl>
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={1}>
                {selectedNode?.businessIdentifierRequired ? (
                  <TextField
                    fullWidth
                    label="Auxiliar (RUT)"
                    value={line.businessIdentifier || ''}
                    onChange={(e) =>
                      handleChange(e, index, 'businessIdentifier')
                    }
                    required
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleEnterPress(index, 'businessIdentifier');
                      }
                    }}
                    inputRef={getLineRefs(index).businessIdentifier}
                  />
                ) : (
                  <div style={{ height: '56px' }} />
                )}
              </Grid>
              <Grid item xs={1.75}>
                <TextField
                  label="Debe"
                  fullWidth
                  style={{ textAlign: 'right' }}
                  value={formatMoney(line.debit)}
                  onChange={(e) => handleChange(e, index, 'debit')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnterPress(index, 'debit');
                    }
                  }}
                  inputRef={getLineRefs(index).debit}
                />
              </Grid>
              <Grid item xs={1.75}>
                <TextField
                  style={{ textAlign: 'right' }}
                  label="Haber"
                  value={formatMoney(line.credit)}
                  onChange={(e) => handleChange(e, index, 'credit')}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleEnterPress(index, 'credit');
                    }
                  }}
                  inputRef={getLineRefs(index).credit}
                />
              </Grid>
              <Grid item xs={0.5}>
                <IconButton
                  onClick={() => handleDeleteLine(index)}
                  size="small"
                >
                  <Delete />
                </IconButton>
              </Grid>
            </Grid>
          );
        })}
        <Divider />
        <Grid container spacing={2} style={{ padding: '4px 0px' }}>
          <Grid item xs={8}>
            <Typography fontWeight="bold">Totales</Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography
              fontWeight="bold"
              color={Math.abs(totalSumEquals) > 1 ? 'red' : 'black'}
              variant="body1"
              style={{ textAlign: 'right' }}
            >
              {formatMoney(
                entry.lines.reduce(
                  (acc: number, line: AccountingEntryLine) => acc + line.debit,
                  0,
                ),
              )}
            </Typography>
          </Grid>
          <Grid item xs={1.5}>
            <Typography
              fontWeight="bold"
              color={Math.abs(totalSumEquals) > 1 ? 'red' : 'black'}
              variant="body1"
              style={{ textAlign: 'right' }}
            >
              {formatMoney(
                entry.lines.reduce(
                  (acc: number, line: AccountingEntryLine) => acc + line.credit,
                  0,
                ),
              )}
            </Typography>
          </Grid>
          <Grid item xs={1} />
        </Grid>
      </div>
    </div>
  );
};

export default EditEntryWithLines;
